import { Renderer, Program, Mesh, Color, Triangle } from 'ogl';

const vertexShader = `
    attribute vec2 uv;
    attribute vec2 position;
    varying vec2 vUv;
    void main() {
    vUv = uv;
    gl_Position = vec4(position, 0.0, 1.0);
}`;

const fragmentShader = `
precision highp float;
uniform float uTime;
uniform vec3 uColor;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uAmplitude;
uniform float uSpeed;
varying vec2 vUv;

void main() {
float mr = min(uResolution.x, uResolution.y);
vec2 uv = (vUv * 2.0 - 1.0) * uResolution.xy / mr;

uv += (uMouse - vec2(0.5)) * uAmplitude;

float d = -uTime * 0.5 * uSpeed;
float a = 0.0;
for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
}
d += uTime * 0.5 * uSpeed;
vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
gl_FragColor = vec4(col, 1.0);}`;

class Incandecence extends HTMLElement {
    constructor({ color = [1, 1, 1], speed = 1.0, amplitude = 0.1, mouseReact = true, background = [1, 1, 1, 1] } = {}) {
        super();

        this.style.display = 'block';
        if (!this.style.width)  this.style.width  = '150%';
        if (!this.style.height) this.style.height = '150%';
        this.style.position = 'absolute';
        this.style.top = '-25%';
        this.style.left = '-25%';


        this._params = { color, speed, amplitude, mouseReact, background };
        this._mouse = { x: 0.5, y: 0.5 };

        this._renderer = null;
        this._gl = null;
        this._program = null;
        this._mesh = null;
        this._animId = null;
        this._resizeObserver = null;

        this._onMouseMove = null;
        this._resize = null;
    }

    connectedCallback() {
        this._init();
    }

    disconnectedCallback() {
        this.destroy();
    }

    _init() {
        const { color, speed, amplitude, background } = this._params;

        this._renderer = new Renderer();
        const gl = this._renderer.gl;
        this._gl = gl;
        gl.clearColor(background[0], background[1], background[2], background[3] ?? 1);

        const resize = () => {
        const w = Math.max(1, this.clientWidth);
        const h = Math.max(1, this.clientHeight);
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        this._renderer.setSize(w * dpr, h * dpr);

        if (this._program) {
            this._program.uniforms.uResolution.value = new Color(
            gl.canvas.width,
            gl.canvas.height,
            gl.canvas.width / gl.canvas.height
            );
        }
        };
        this._resize = resize;
        resize();

        const geometry = new Triangle(gl);

        this._program = new Program(gl, {
        vertex: vertexShader,
        fragment: fragmentShader,
        uniforms: {
            uTime: { value: 0 },
            uColor: { value: new Color(...color) },
            uResolution: { value: new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height) },
            uMouse: { value: new Float32Array([this._mouse.x, this._mouse.y]) },
            uAmplitude: { value: amplitude },
            uSpeed: { value: speed },
        },
        });

        this._mesh = new Mesh(gl, { geometry, program: this._program });

        const animate = (t) => {
        this._animId = requestAnimationFrame(animate);
        this._program.uniforms.uTime.value = t * 0.001;
        this._renderer.render({ scene: this._mesh });
        };
        this._animId = requestAnimationFrame(animate);

        this.appendChild(gl.canvas);

        if (this._params.mouseReact) {
        this._onMouseMove = (e) => {
            const rect = this.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = 1 - (e.clientY - rect.top) / rect.height;
            this._mouse = { x, y };
            const u = this._program.uniforms.uMouse.value;
            u[0] = x; u[1] = y;
        };
        this.addEventListener('mousemove', this._onMouseMove);
        }

        this._resizeObserver = new ResizeObserver(() => resize());
        this._resizeObserver.observe(this);
        window.addEventListener('resize', resize);
    }

    set color(c) {
        this._params.color = c;
        if (this._program) this._program.uniforms.uColor.value = new Color(...c);
    }
    set speed(s) {
        this._params.speed = s;
        if (this._program) this._program.uniforms.uSpeed.value = s;
    }
    set amplitude(a) {
        this._params.amplitude = a;
        if (this._program) this._program.uniforms.uAmplitude.value = a;
    }
    set mouseReact(v) {
        if (v === this._params.mouseReact) return;
        this._params.mouseReact = v;
        if (v && !this._onMouseMove) {
        this._onMouseMove = (e) => {
            const rect = this.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = 1 - (e.clientY - rect.top) / rect.height;
            this._mouse = { x, y };
            const u = this._program.uniforms.uMouse.value;
            u[0] = x; u[1] = y;
        };
        this.addEventListener('mousemove', this._onMouseMove);
        } else if (!v && this._onMouseMove) {
        this.removeEventListener('mousemove', this._onMouseMove);
        this._onMouseMove = null;
        }
    }

    start() {
        if (!this._animId) {
        const again = (t) => {
            this._animId = requestAnimationFrame(again);
            this._program.uniforms.uTime.value = t * 0.001;
            this._renderer.render({ scene: this._mesh });
        };
        this._animId = requestAnimationFrame(again);
        }
    }

    stop() {
        if (this._animId) {
        cancelAnimationFrame(this._animId);
        this._animId = null;
        }
    }

    destroy() {
        this.stop();
        if (this._resizeObserver) { this._resizeObserver.disconnect(); this._resizeObserver = null; }
        window.removeEventListener('resize', this._resize || (()=>{}));
        if (this._onMouseMove) { this.removeEventListener('mousemove', this._onMouseMove); this._onMouseMove = null; }
        if (this._gl) {
        const ext = this._gl.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
        }
        if (this.firstChild) this.removeChild(this.firstChild);
        this._renderer = null;
        this._gl = null;
        this._program = null;
        this._mesh = null;
    }
}

customElements.define('incandecence-canvas', Incandecence);
export { Incandecence };